import { Typography } from "@mui/material";
import { Box, Stack } from "@mui/system";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
} from "recharts";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import IconButton from "@mui/material/IconButton";

const ITEMS_PER_PAGE = 10;

const BAR_COLORS = [
  "#B8E0E8",
  "#F4C0BB",
  "#C8E4A0",
  "#F5DFA0",
  "#C8BDE8",
  "#B8C4F0",
  "#F4C8A0",
  "#A8DCE0",
  "#C0E8D8",
  "#F0C8D8",
];

type ChartData = {
  name: string;
  value: number;
};

type CustomLabelProps = {
  x?: number;
  y?: number;
  width?: number;
  value?: number;
};

const CustomLabel = ({
  x = 0,
  y = 0,
  width = 0,
  value = 0,
}: CustomLabelProps) => (
  <text
    x={x + width / 2}
    y={y - 6}
    fill="#555"
    textAnchor="middle"
    fontSize={11}
    fontWeight={500}
    fontFamily="Roboto"
  >
    {Math.round(value)}%
  </text>
);

type CustomXAxisTickProps = {
  x?: number;
  y?: number;
  payload?: { value: string };
};

const CustomXAxisTick = ({ x = 0, y = 0, payload }: CustomXAxisTickProps) => {
  const words = (payload?.value ?? "").split(" ");
  const lines: string[] = [];
  let current = "";
  for (const word of words) {
    if (current && (current + " " + word).length > 14) {
      lines.push(current);
      current = word;
    } else {
      current = current ? current + " " + word : word;
    }
  }
  if (current) lines.push(current);

  return (
    <g transform={`translate(${x},${y})`}>
      {lines.map((line, i) => (
        <text
          key={i}
          x={0}
          y={12 + i * 14}
          textAnchor="middle"
          fill="#555"
          fontSize={11}
          fontFamily="Roboto"
        >
          {line}
        </text>
      ))}
    </g>
  );
};

type BudgetControlChartProps = {
  data: ChartData[];
  showOveruseOnly: boolean;
  onBarClick: (item: ChartData) => void;
  activeBar: string | null;
};

export default function BudgetControlChart({
  data,
  showOveruseOnly,
  onBarClick,
  activeBar,
}: BudgetControlChartProps) {
  const [page, setPage] = useState(0);

  const filteredData = useMemo(() => {
    if (!data || data.length === 0) return [];
    const base = showOveruseOnly ? data.filter((d) => d.value >= 100) : data;
    return base;
  }, [data, showOveruseOnly]);

  // Reset to page 0 whenever the filtered dataset changes (toggle or data refresh)
  useEffect(() => {
    setPage(0);
  }, [filteredData]);

  // When a NEW bar is selected (not deselected, not same bar re-clicked),
  // navigate to the page that contains it
  const prevActiveBarRef = useRef<string | null>(null);
  useEffect(() => {
    if (activeBar !== null && activeBar !== prevActiveBarRef.current) {
      const idx = filteredData.findIndex((d) => d.name === activeBar);
      if (idx !== -1) {
        setPage(Math.floor(idx / ITEMS_PER_PAGE));
      }
    }
    prevActiveBarRef.current = activeBar;
  }, [activeBar, filteredData]);

  const totalPages = Math.ceil(filteredData.length / ITEMS_PER_PAGE);

  const pagedData = useMemo(() => {
    const start = page * ITEMS_PER_PAGE;
    return filteredData.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredData, page]);

  const maxValue = useMemo(() => {
    if (pagedData.length === 0) return 125;
    return Math.max(125, Math.max(...pagedData.map((d) => d.value)));
  }, [pagedData]);

  const yAxisTicks = useMemo(() => {
    const base = [0, 25, 50, 75, 100, 125];
    if (maxValue <= 125) return base;
    // if data exceeds 125, add extra ticks in 25 increments
    const extra = [...base];
    let next = 150;
    while (next <= maxValue + 10) {
      extra.push(next);
      next += 25;
    }
    return extra;
  }, [maxValue]);

  const showingStart = page * ITEMS_PER_PAGE + 1;
  const showingEnd = Math.min((page + 1) * ITEMS_PER_PAGE, filteredData.length);

  if (filteredData.length === 0) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height={380}
        gap={1}
      >
        <Typography fontSize="14px" color="text.secondary">
          No data found
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <ResponsiveContainer width="100%" height={380}>
        <BarChart
          data={pagedData}
          margin={{ top: 20, right: 50, left: 10, bottom: 10 }}
          barCategoryGap="25%"
        >
          <CartesianGrid vertical={false} stroke="#EEEEEE" />

          <XAxis
            dataKey="name"
            axisLine={false}
            tickLine={false}
            interval={0}
            height={60}
            tick={<CustomXAxisTick />}
          />

          <YAxis
            axisLine={false}
            tickLine={false}
            orientation="right"
            domain={[0, yAxisTicks[yAxisTicks.length - 1]]}
            ticks={yAxisTicks}
            tickFormatter={(v) => `${v}%`}
            tick={(props: any) => {
              const { x, y, payload } = props;
              const isHundred = payload.value === 100;
              return (
                <text
                  x={x}
                  y={y}
                  dy={4}
                  textAnchor="start"
                  fontSize={11}
                  fontFamily="Roboto"
                  fontWeight={isHundred ? 600 : 400}
                  fill={isHundred ? "#E53935" : "#777"}
                >
                  {payload.value}%
                </text>
              );
            }}
          />

          {/* 100% reference line — red solid line */}
          <ReferenceLine y={100} stroke="#E53935" strokeWidth={1.5} />

          <Bar
            dataKey="value"
            radius={[0, 0, 0, 0]}
            onClick={(barData) => onBarClick(barData)}
            style={{ cursor: "pointer" }}
          >
            <LabelList content={<CustomLabel />} />
            {pagedData.map((item, index) => (
              <Cell
                key={index}
                fill={BAR_COLORS[index % BAR_COLORS.length]}
                opacity={
                  activeBar === null || activeBar === item.name ? 1 : 0.3
                }
                style={{ transition: "opacity 0.25s ease" }}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      {/* Pagination row */}
      {filteredData.length > 0 && (
        <Box
          display="flex"
          alignItems="center"
          px={2}
          py={1}
          sx={{ borderTop: "1px solid #E0E0E0", position: "relative" }}
        >
          <Typography fontSize="12px" color="text.secondary">
            Showing {showingStart}-{showingEnd} of {filteredData.length}
          </Typography>

          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
            }}
          >
            <IconButton
              size="small"
              onClick={() => setPage(0)}
              disabled={page === 0}
            >
              <FirstPageIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>

            {Array.from({ length: totalPages }, (_, i) => (
              <Box
                key={i}
                onClick={() => setPage(i)}
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  fontSize: "13px",
                  fontWeight: page === i ? 500 : 400,
                  backgroundColor:
                    page === i ? "secondary.main" : "transparent",
                  color: page === i ? "#fff" : "text.primary",
                  "&:hover": {
                    backgroundColor:
                      page === i ? "secondary.main" : "action.hover",
                  },
                }}
              >
                {i + 1}
              </Box>
            ))}

            <IconButton
              size="small"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => setPage(totalPages - 1)}
              disabled={page >= totalPages - 1}
            >
              <LastPageIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Box>
      )}
    </>
  );
}
